import React, {useEffect, useLayoutEffect} from 'react';
import {
  CANVAS_USER_SCROLL_ENDED,
  CANVAS_USER_SCROLL_STARTED,
  CANVAS_SCROLL_POSITION_UPDATED,
  CANVAS_APP_NODE_HOVERED,
  CANVAS_APP_NODE_CLICKED,
} from '@packages/systems/designer/actions';
import type {UiNodeStateType} from '@packages/systems/core/records/UiNodeState';
import {nativeIdPathsEqual} from '@packages/utilities/platform';
import {
  getClampedCanvasScrollPosition,
  isScrollingOverScrollableElement,
  type CanvasScrollStateShape,
} from '@packages/systems/designer/canvas';

import {dispatchInDesigner} from '../utils/dispatchInDesigner';
import {useCanvasAppScrollResize} from './useCanvasAppScrollResize';
/* eslint-disable webflow/package-boundaries */
// See epic https://webflow.atlassian.net/browse/DESUX-8149 to pay off this 👇 tech debt
import {onPreviewClick} from '../../../../public/js/designer-flux/utils/PreviewUtils';
/* eslint-enable webflow/package-boundaries */
import {ExpressionElement} from '@packages/systems/wfdl-ast';
import {elementClickedOnCanvas} from '../utils/elementClickedOnCanvas';
import {linkClickedOnCanvas} from '../utils/linkClickedOnCanvas';
import {
  DESIGNER_WORKFLOWS,
  DesignerWorkflow,
} from '@packages/systems/designer/workflows';
import {Component} from '@packages/systems/wfdl';
import {resolveNodePathAndBounds} from '../utils/resolveNodePathAndBounds';
import {WF_DATA_ID_ATTR} from '@packages/utilities/html';
import {useWheelEventInterceptor} from '@packages/systems/designer/wheel-event-interceptor';

const shouldDispatchHoverEvent = (
  hoveredNativeIdPath: string[] | null,
  currentNodePath: string[]
) => {
  return (
    hoveredNativeIdPath &&
    !nativeIdPathsEqual(hoveredNativeIdPath, currentNodePath)
  );
};

/**
 * Create all event handlers that are passed to Canvas Body
 *
 * Currently used only for this purpose for Canvas Body that is inside Canvas App.
 */
export const useBodyEventHandlers = ({
  canvasScrollState,
  pageId,
  canvasResizeState,
  isOpenCanvasActive,
  uiNodeState,
  workflow,
  siteBasePath,
  components,
  paginationPageNumbers,
}: {
  canvasScrollState: CanvasScrollStateShape;
  pageId: string | undefined;
  canvasResizeState: {width: number; height: number; scale: number} | undefined;
  isOpenCanvasActive: boolean;
  siteBasePath: string | null | undefined;
  components: Component.ComponentMap | undefined;
  paginationPageNumbers: {[key: string]: number} | undefined;
  uiNodeState: UiNodeStateType | undefined;
  workflow: DesignerWorkflow | undefined;
}) => {
  const {canvasScrollPosition, canvasScrollHeight, canvasScrollWidth} =
    canvasScrollState;
  const didCanvasScrollFromScrollbarOrAutoscroll =
    canvasScrollState?.userScrollingFrom === 'scrollbar' ||
    canvasScrollState?.userScrollingFrom === 'autoscroll';
  const canvasScrollPositionTop = canvasScrollPosition.top || 0;
  const canvasScrollPositionLeft = canvasScrollPosition.left || 0;
  const hoveredNativeIdPath = React.useRef(
    uiNodeState?.hoveredNativeIdPath || null
  );

  useEffect(() => {
    hoveredNativeIdPath.current = uiNodeState?.hoveredNativeIdPath || null;
  }, [uiNodeState?.hoveredNativeIdPath]);

  // We are storing scrolling state locally, so we don't have delays in responsiveness of scroll due to the message communication between
  // Canvas App and Designer App. Also, we are storying this in variable and not state as we want immediate updates and not batched by React
  // to have as smooth experience as possible.
  const userControlledScrollState = React.useRef<{
    top: number;
    left: number;
  }>({
    top: canvasScrollPositionTop,
    left: canvasScrollPositionLeft,
  });

  useLayoutEffect(() => {
    // Open Canvas is using panning and zooming so we disable scrolling
    if (isOpenCanvasActive) return;
    // We are syncing the scroll position between Flux state and iframe on "mount"
    // because by default it will be scrolled at the top. Before we were only syncing
    // on wheel but the result was that this iframe was loading scolled at the top
    // and then moving to the right position when wheel event was fired.
    // See repro here: https://github.com/webflow/webflow/pull/79624#pullrequestreview-2627684596
    window.scrollTo({
      top: canvasScrollPositionTop,
      left: canvasScrollPositionLeft,
    });
    // We are disabling ESLint checks here because we really want to execute
    // this hooks only once, when component first "mounts".
    // eslint-disable-next-line react-hooks/react-compiler -- pre-ratchet baseline (BLOOP-3033)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useCanvasAppScrollResize({
    canvasScrollHeight,
    canvasScrollWidth,
    pageId,
  });

  const wheelEventInterceptor = useWheelEventInterceptor({
    onStart: () => {
      dispatchInDesigner({
        type: CANVAS_USER_SCROLL_STARTED,
        payload: {userScrollingFrom: 'canvas'},
      });
    },
    onEnd: () => {
      dispatchInDesigner({type: CANVAS_USER_SCROLL_ENDED});
    },
  });

  React.useLayoutEffect(() => {
    // Open Canvas is using panning and zooming so we disable scrolling
    if (isOpenCanvasActive) return;

    if (didCanvasScrollFromScrollbarOrAutoscroll) {
      // should only be evoked if user is doing scrolling using scrollbar directly
      window.scrollTo({
        top: canvasScrollPositionTop,
        left: canvasScrollPositionLeft,
      });

      // We need to ensure that our "inner" scroll state gets updated when user is scrolling using scrollbar,
      // otherwise it will revert to its former value when we start scrolling with the wheel again.
      userControlledScrollState.current = {
        top: canvasScrollPositionTop,
        left: canvasScrollPositionLeft,
      };
    }
  }, [
    isOpenCanvasActive,
    canvasScrollPositionLeft,
    canvasScrollPositionTop,
    didCanvasScrollFromScrollbarOrAutoscroll,
  ]);

  const onWheel = React.useCallback(
    (event: WheelEvent) => {
      wheelEventInterceptor.intercept();

      if (isScrollingOverScrollableElement(event) || !canvasResizeState) {
        return;
      }

      event.stopPropagation();

      const {left = 0, top = 0} = userControlledScrollState.current || {};

      const newTargetPosition = {
        left: left + (event.deltaX ? event.deltaX : 0),
        top: top + (event.deltaY ? event.deltaY : 0),
      };

      const clampedNewTargetPosition = getClampedCanvasScrollPosition(
        canvasResizeState,
        newTargetPosition,
        canvasScrollWidth,
        canvasScrollHeight
      );

      if (
        clampedNewTargetPosition.top !==
          userControlledScrollState.current?.top ||
        clampedNewTargetPosition.left !==
          userControlledScrollState.current?.left
      ) {
        dispatchInDesigner({
          type: CANVAS_SCROLL_POSITION_UPDATED,
          payload: clampedNewTargetPosition,
        });

        userControlledScrollState.current = clampedNewTargetPosition;

        // we are performing scrolling from here instead of relying on eventual update of the scroll
        // location from Flux to provide the smoothest experience with scrolling for our users. This
        // means that there might be a slight asynchrony between the scroll position in Flux and the
        // scroll position in the DOM, but it's preferrable over a really bad experience for the customer
        // (keeping both exactly on sync leads to scroll jumps and glitchy behavior).
        window.scrollTo(clampedNewTargetPosition);
      }
    },
    [
      wheelEventInterceptor,
      canvasResizeState,
      canvasScrollWidth,
      canvasScrollHeight,
    ]
  );

  // Attach wheel event listener to the window to capture all wheel events
  React.useEffect(() => {
    // Open Canvas is using panning and zooming so we disable scrolling
    if (isOpenCanvasActive) return;

    window.addEventListener('wheel', onWheel);
    return () => {
      window.removeEventListener('wheel', onWheel);
    };
  }, [isOpenCanvasActive, onWheel]);

  const getTargetElement = (
    event: React.MouseEvent<HTMLElement, MouseEvent>
  ): HTMLElement | null =>
    event.target instanceof HTMLElement ? event.target : null;

  /**
   * Checks if an event target is within a CodeIsland custom element.
   * CodeIsland elements intercept and re-dispatch events, which makes them synthetic (isTrusted: false).
   */
  const isEventFromCodeIsland = (
    targetElement: HTMLElement | null
  ): boolean => {
    if (!targetElement) return false;

    // Check if the element is within a code-island custom element
    const codeIslandElement = targetElement.closest('code-island');
    return Boolean(codeIslandElement);
  };

  const onMouseMove = React.useCallback(
    (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
      if (workflow !== DESIGNER_WORKFLOWS.COMMENT) return;
      event.stopPropagation();

      const targetElement = getTargetElement(event);
      if (!targetElement) return;

      const {nativeIdPath} = resolveNodePathAndBounds(
        targetElement,
        components
      );

      if (shouldDispatchHoverEvent(hoveredNativeIdPath.current, nativeIdPath)) {
        dispatchInDesigner({
          type: CANVAS_APP_NODE_HOVERED,
          payload: {nativeIdPath},
        });
      }
    },
    [hoveredNativeIdPath, workflow, components]
  );

  const onMouseLeave = React.useCallback(() => {
    if (workflow !== DESIGNER_WORKFLOWS.COMMENT) return;
    // Clears the hover state when the mouse leaves the canvas
    dispatchInDesigner({
      type: CANVAS_APP_NODE_HOVERED,
      payload: {nativeIdPath: []},
    });
  }, [workflow]);

  const onClick = React.useCallback(
    async (event: React.MouseEvent<HTMLElement>) => {
      if (workflow === DESIGNER_WORKFLOWS.COMMENT) {
        /**
         * Determines if the current context is a Cypress test environment
         * by checking the URL parameter 'cypressTest=true'.
         * This URL parameter approach is used because the Canvas App runs
         * in a separate origin from the main application.
         */
        const isCypressTest =
          typeof window !== 'undefined' &&
          new URLSearchParams(window.location.search).get('cypressTest') ===
            'true';

        // Some custom code might be triggering its own custom click events,
        // causing our listener to react to them. However, we only want to
        // respond to user-triggered events. This check ensures that we ignore
        // programmatically dispatched events while still allowing Cypress tests.
        // CodeIsland elements intercept and re-dispatch events, making them synthetic,
        // so we also allow events from CodeIsland elements.
        const targetElement = getTargetElement(event);
        const isFromCodeIsland = isEventFromCodeIsland(targetElement);
        if (!event.isTrusted && !isCypressTest && !isFromCodeIsland) return;
        event.preventDefault();

        if (!targetElement) return;

        const {nativeIdPath, bounds} = resolveNodePathAndBounds(
          targetElement,
          components
        );

        const isCustomCodeGeneratedElement =
          !targetElement.hasAttribute(WF_DATA_ID_ATTR);

        const {clientX, clientY} = event;
        const {top, left, width, height} = bounds;
        dispatchInDesigner({
          type: CANVAS_APP_NODE_CLICKED,
          payload: {
            nativeIdPath,
            lastClickCoordinates: [
              ((clientX - left) * 100) / width,
              ((clientY - top) * 100) / height,
            ],
            clickedInCustomCodeElement: isCustomCodeGeneratedElement,
          },
        });
      } else {
        // This is a technical check to make TS happy
        // in real-world scenario this should never happen as user would not be able to click on canvas before components data arrive
        // as we are not rendering canvas then. However as hooks should be initialized before any if checks TS thinks that there might be a case
        // where user clicks on canvas before components data arrive. Same with paginationPageNumbers data.
        if (!components || !paginationPageNumbers) return;
        const onInternalLinkClicked = async (href: string) => {
          linkClickedOnCanvas({href});
        };

        const onElementClicked = (element: ExpressionElement) => {
          elementClickedOnCanvas({element});
        };
        await onPreviewClick({
          components,
          event,
          paginationPageNumbers,
          dispatch: dispatchInDesigner,
          hrefPrefix: siteBasePath,
          onInternalLinkClicked,
          onElementClicked,
        });
      }
    },
    [components, paginationPageNumbers, siteBasePath, workflow]
  );

  return React.useMemo(
    () => ({
      onMouseMove,
      onMouseLeave,
      onClick,
    }),
    [onMouseMove, onMouseLeave, onClick]
  );
};
